// ABOUTME: Mock provider for testing and development without backend
// ABOUTME: Implements all provider interfaces with in-memory storage

import type {
  BackendProvider,
  IAuthProvider,
  IDatabaseProvider,
  IStorageProvider,
  AuthResult,
  User,
  Session,
  AuthStateChangeCallback,
  QueryOptions,
  DatabaseResult,
  RealtimeCallback,
  UploadOptions,
  UploadResult,
  FileMetadata,
} from '../interfaces'

/**
 * Mock authentication provider
 *
 * Stores session in memory, no actual authentication
 */
class MockAuthProvider implements IAuthProvider {
  private currentUser: User | null = null
  private currentSession: Session | null = null
  private listeners: AuthStateChangeCallback[] = []

  async signUp(email: string, _password: string, metadata?: Record<string, any>): Promise<AuthResult> {
    const user: User = {
      id: crypto.randomUUID(),
      email,
      emailConfirmed: true,
      metadata: metadata || {},
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    const session: Session = {
      accessToken: 'mock-access-token',
      refreshToken: 'mock-refresh-token',
      expiresAt: new Date(Date.now() + 3600000), // 1 hour
      user,
    }

    this.currentUser = user
    this.currentSession = session
    this.notifyListeners(session, user)

    return { user, session }
  }

  async signIn(email: string, password: string): Promise<AuthResult> {
    // Mock sign in - just create a session
    return this.signUp(email, password)
  }

  async signOut(): Promise<void> {
    this.currentUser = null
    this.currentSession = null
    this.notifyListeners(null, null)
  }

  async getSession(): Promise<Session | null> {
    return this.currentSession
  }

  async getUser(): Promise<User | null> {
    return this.currentUser
  }

  onAuthStateChange(callback: AuthStateChangeCallback): () => void {
    this.listeners.push(callback)
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback)
    }
  }

  async resetPassword(email: string): Promise<{ error?: Error }> {
    console.log('[Mock] Password reset email sent to:', email)
    return {}
  }

  async updateUser(metadata: Record<string, any>): Promise<{ user: User | null; error?: Error }> {
    if (!this.currentUser) {
      return { user: null, error: new Error('No user logged in') }
    }

    this.currentUser = {
      ...this.currentUser,
      metadata: { ...this.currentUser.metadata, ...metadata },
      updatedAt: new Date(),
    }

    return { user: this.currentUser }
  }

  private notifyListeners(session: Session | null, user: User | null): void {
    for (const listener of this.listeners) {
      listener(session, user)
    }
  }
}

/**
 * Mock database provider
 *
 * Stores data in memory, no persistence
 */
class MockDatabaseProvider implements IDatabaseProvider {
  private tables: Map<string, Map<string, any>> = new Map()
  private listeners: Map<string, Array<{ callback: RealtimeCallback<any>; filters?: any[] }>> = new Map()

  async query<T>(table: string, options?: QueryOptions): Promise<T[]> {
    const tableData = this.tables.get(table) || new Map()
    let results = Array.from(tableData.values())

    // Apply filters
    if (options?.filters) {
      results = results.filter((item) => {
        return options.filters!.every((filter) => {
          const value = item[filter.field]
          switch (filter.operator) {
            case 'eq': return value === filter.value
            case 'neq': return value !== filter.value
            case 'gt': return value > filter.value
            case 'gte': return value >= filter.value
            case 'lt': return value < filter.value
            case 'lte': return value <= filter.value
            case 'in': return Array.isArray(filter.value) && filter.value.includes(value)
            case 'contains': return typeof value === 'string' && value.includes(filter.value)
            default: return true
          }
        })
      })
    }

    // Apply ordering
    if (options?.orderBy) {
      results.sort((a, b) => {
        const aVal = a[options.orderBy!.field]
        const bVal = b[options.orderBy!.field]
        const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
        return options.orderBy!.ascending ? comparison : -comparison
      })
    }

    // Apply pagination
    if (options?.offset !== undefined) {
      results = results.slice(options.offset)
    }
    if (options?.limit !== undefined) {
      results = results.slice(0, options.limit)
    }

    return results as T[]
  }

  async get<T>(table: string, id: string): Promise<T | null> {
    const tableData = this.tables.get(table)
    if (!tableData) return null
    return (tableData.get(id) as T) || null
  }

  async insert<T>(table: string, data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>): Promise<DatabaseResult<T>> {
    if (!this.tables.has(table)) {
      this.tables.set(table, new Map())
    }

    const tableData = this.tables.get(table)!
    const record = {
      id: crypto.randomUUID(),
      ...data,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as T

    tableData.set((record as any).id, record)
    this.notifyListeners(table, 'INSERT', record)

    return { data: record }
  }

  async update<T>(table: string, id: string, data: Partial<T>): Promise<DatabaseResult<T>> {
    const tableData = this.tables.get(table)
    if (!tableData || !tableData.has(id)) {
      return { data: null, error: new Error('Record not found') }
    }

    const existing = tableData.get(id)
    const updated = {
      ...existing,
      ...data,
      updatedAt: new Date(),
    } as T

    tableData.set(id, updated)
    this.notifyListeners(table, 'UPDATE', updated)

    return { data: updated }
  }

  async delete(table: string, id: string): Promise<{ error?: Error }> {
    const tableData = this.tables.get(table)
    if (!tableData || !tableData.has(id)) {
      return { error: new Error('Record not found') }
    }

    const record = tableData.get(id)
    tableData.delete(id)
    this.notifyListeners(table, 'DELETE', record)

    return {}
  }

  async batchInsert<T>(table: string, data: Array<Omit<T, 'id' | 'createdAt' | 'updatedAt'>>): Promise<DatabaseResult<T[]>> {
    const results: T[] = []
    for (const item of data) {
      const result = await this.insert<T>(table, item)
      if (result.data) {
        results.push(result.data)
      }
    }
    return { data: results }
  }

  subscribe<T>(table: string, callback: RealtimeCallback<T>): () => void {
    if (!this.listeners.has(table)) {
      this.listeners.set(table, [])
    }

    const listenerList = this.listeners.get(table)!
    listenerList.push({ callback })

    return () => {
      const index = listenerList.findIndex((l) => l.callback === callback)
      if (index >= 0) {
        listenerList.splice(index, 1)
      }
    }
  }

  async transaction<T>(operations: Array<{ type: 'insert' | 'update' | 'delete'; table: string; data: any }>): Promise<DatabaseResult<T>> {
    const results: any[] = []

    for (const op of operations) {
      if (op.type === 'insert') {
        const result = await this.insert(op.table, op.data)
        if (result.error) return { data: null, error: result.error }
        results.push(result.data)
      } else if (op.type === 'update') {
        const result = await this.update(op.table, op.data.id, op.data)
        if (result.error) return { data: null, error: result.error }
        results.push(result.data)
      } else if (op.type === 'delete') {
        const result = await this.delete(op.table, op.data.id)
        if (result.error) return { data: null, error: result.error }
      }
    }

    return { data: results as T }
  }

  private notifyListeners<T>(table: string, event: 'INSERT' | 'UPDATE' | 'DELETE', data: T): void {
    const listeners = this.listeners.get(table)
    if (!listeners) return

    for (const listener of listeners) {
      listener.callback(event, data)
    }
  }
}

/**
 * Mock storage provider
 *
 * Stores files as data URLs in memory
 */
class MockStorageProvider implements IStorageProvider {
  private buckets: Map<string, Map<string, { blob: Blob; metadata: FileMetadata }>> = new Map()

  async upload(bucket: string, path: string, file: Blob | File, options?: UploadOptions): Promise<UploadResult> {
    if (!this.buckets.has(bucket)) {
      this.buckets.set(bucket, new Map())
    }

    const bucketData = this.buckets.get(bucket)!
    const metadata: FileMetadata = {
      name: path.split('/').pop() || path,
      size: file.size,
      contentType: file.type || options?.contentType || 'application/octet-stream',
      lastModified: new Date(),
      url: `mock://${bucket}/${path}`,
    }

    bucketData.set(path, { blob: file, metadata })

    return {
      path,
      url: metadata.url,
    }
  }

  async download(bucket: string, path: string): Promise<Blob> {
    const bucketData = this.buckets.get(bucket)
    if (!bucketData || !bucketData.has(path)) {
      throw new Error('File not found')
    }

    return bucketData.get(path)!.blob
  }

  getPublicUrl(bucket: string, path: string): string {
    return `mock://${bucket}/${path}`
  }

  async delete(bucket: string, path: string): Promise<{ error?: Error }> {
    const bucketData = this.buckets.get(bucket)
    if (!bucketData || !bucketData.has(path)) {
      return { error: new Error('File not found') }
    }

    bucketData.delete(path)
    return {}
  }

  async list(bucket: string, path?: string): Promise<FileMetadata[]> {
    const bucketData = this.buckets.get(bucket)
    if (!bucketData) return []

    const prefix = path ? `${path}/` : ''
    const results: FileMetadata[] = []

    for (const [filePath, { metadata }] of bucketData.entries()) {
      if (filePath.startsWith(prefix)) {
        results.push(metadata)
      }
    }

    return results
  }

  async createSignedUrl(bucket: string, path: string, expiresIn: number): Promise<{ url: string; error?: Error }> {
    return { url: `mock://${bucket}/${path}?signed=true&expires=${expiresIn}` }
  }

  async getMetadata(bucket: string, path: string): Promise<FileMetadata | null> {
    const bucketData = this.buckets.get(bucket)
    if (!bucketData || !bucketData.has(path)) {
      return null
    }

    return bucketData.get(path)!.metadata
  }
}

/**
 * Create a mock backend provider
 *
 * Useful for development and testing without requiring real backend
 */
export function createMockProvider(): BackendProvider {
  return {
    auth: new MockAuthProvider(),
    database: new MockDatabaseProvider(),
    storage: new MockStorageProvider(),
  }
}
